import { createContext, useEffect, useState } from 'react';
import type { FC } from 'react';
import { LOCAL_STORAGE_KEYS, SESSION_STORAGE_KEYS } from 'src/constants';
import { ContextProviderProps } from 'src/types/contextProviderProps';
import { Activity } from 'src/types/activity';
import { CategoryMaxDiscount } from 'src/types/categoryMaxDiscount';
import api from '../api/Api';
import useAuth from 'src/hooks/useAuth';

interface Globals {
  customerPresent: boolean;
  openOrderSearchString: string;
  tasksDue: Activity[];
  categoryMaxDiscounts: CategoryMaxDiscount[];
}

const initialGlobals: Globals = {
  customerPresent: false,
  openOrderSearchString: '',
  tasksDue: [],
  categoryMaxDiscounts: [],
};

export interface GlobalsContextValue {
  globals: Globals;
  setCustomerPresent: (isCustomerPresent: boolean) => void;
  setOpenOrdersSearchString?: (searchString: string) => void;
}

const GlobalsContext = createContext<GlobalsContextValue>({
  globals: initialGlobals,
  setCustomerPresent: () => {},
  setOpenOrdersSearchString: () => {},
});

export const GlobalsProvider: FC<ContextProviderProps> = (props) => {
  const { children } = props;
  const [globals, setGlobals] = useState<Globals>(initialGlobals);
  const { user } = useAuth();

  useEffect(() => {
    //Fetch from local storage
    if (!user) return;
    const custPresent = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.CustomerPresent));
    if (custPresent) {
      setGlobals((prevGlobals) => ({ ...prevGlobals, customerPresent: custPresent }));
    }
    if (sessionStorage.getItem(SESSION_STORAGE_KEYS.OpenOrderSearchString)) {
      setGlobals((prevGlobals) => ({ ...prevGlobals, openOrderSearchString: sessionStorage.getItem('openOrderSearchString') }));
    }
    api.get<CategoryMaxDiscount[]>('/sfa/discounts').then((res) => {
      setGlobals((prevGlobals) => ({ ...prevGlobals, categoryMaxDiscounts: res }));
    }).catch((err) => {
      console.error('Error fetching category discounts:', err);
    });
  }, [user]);

  const setCustomerPresent = (isCustomerPresent: boolean): void => {
    setGlobals((prevGlobals) => ({ ...prevGlobals, customerPresent: isCustomerPresent }));
    localStorage.setItem(LOCAL_STORAGE_KEYS.CustomerPresent, JSON.stringify(isCustomerPresent));
  };

  const setOpenOrdersSearchString = (searchString: string): void => {
    setGlobals((prevGlobals) => ({ ...prevGlobals, openOrderSearchString: searchString }));
    sessionStorage.setItem(SESSION_STORAGE_KEYS.OpenOrderSearchString, searchString);
  };

  return (
    <GlobalsContext.Provider
      value={{
        globals,
        setCustomerPresent,
        setOpenOrdersSearchString,
      }}
    >
      {children}
    </GlobalsContext.Provider>
  );
};

export const GlobalsConsumer = GlobalsContext.Consumer;

export default GlobalsContext;
